import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SnackbarService } from 'app/core';
import { HttpParams } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { HttpService } from 'app/core';
import { CsvService } from 'app/core/services/csv.service';
import { GeneralService } from 'app/core';
import { ExcelService } from 'app/core/services/excel.service';
import { XlsxResponseModel } from 'app/core';
import { SettingsProvider } from 'app/core';

export interface DialogData {
    importConfiguration: string;
    importApproval: boolean;
    importFileExt: 'csv' | 'xlsx' | 'both' | 'img';
    additionalParams: string;
    title?: string;
    warningText?: string;
    useTemplate?: boolean;
}

@Component({
    templateUrl: 'import-dialog.component.html',
    styleUrls: ['import-dialog.component.scss'],
})
export class ImportDialogComponent {
    fileToUpload: File = null;
    isHeadersValid = true;
    isFileValid = true;
    canImport = false;

    uploading = false;

    constructor(
        public snackBar: SnackbarService,
        private http: HttpService,
        private csv: CsvService,
        private excel: ExcelService,
        public dialogRef: MatDialogRef<ImportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData,
        public generalService: GeneralService,
        public settingsService: SettingsProvider
    ) {}

    close(): void {
        this.dialogRef.close({confirm: false});
    }

    onNoClick(): void {
        this.dialogRef.close({ confirm: false });
    }

    confirmFromDialog(response: any): void {
        if (response !== undefined && response !== null) {
            this.dialogRef.close(response);
        } else {
            this.dialogRef.close({ confirm: true });
        }
    }

    upload() {
        this.uploading = true;

        if (!this.data.useTemplate) {
            this.sendFile();
            return;
        }
        const templateUrl = `${this.settingsService.configuration.url}${env.ImportDialogService.apiUrlImportTemplate}`;
        const params = new HttpParams()
            .set('configurationName', `${this.data.importConfiguration}`)
            .set('contentType', 'application/json');
        return this.http
            .getWithParams(templateUrl, params)
            .subscribe((response) => {
                let headersRow;
                if (this.csv.isCSVFile(this.fileToUpload)) {
                    const reader = new FileReader();
                    reader.readAsText(this.fileToUpload);

                    reader.onload = () => {
                        const csvData = reader.result;
                        const csvRecordsArray = (<string>csvData).split(
                            /\r\n|\n/
                        );
                        headersRow = this.csv.getHeaderArray(csvRecordsArray);
                        if (
                            this.csv.validateHeaders(response.Body, headersRow)
                        ) {
                            this.sendFile();
                        } else {
                            this.uploading = false;
                            this.isHeadersValid = false;
                            this.snackBar.info(
                                'Importowany plik nie jest zgodny z szablonem importu'
                            );
                        }
                    };
                } else if (this.excel.isXlsxFile(this.fileToUpload)) {
                    const reader = new FileReader();
                    reader.readAsBinaryString(this.fileToUpload);
                    reader.onload = async () => {
                        const xlsxData = reader.result;
                        headersRow = await this.excel.getHeaderArray(xlsxData);
                        if (
                            this.excel.validateHeaders(
                                response.body,
                                headersRow
                            )
                        ) {
                            this.sendFile();
                        } else {
                            this.uploading = false;
                            this.isHeadersValid = false;
                            this.snackBar.info(
                                'Importowany plik nie jest zgodny z szablonem importu'
                            );
                        }
                    };
                }
            });
    }

    private sendFile() {
        const url = `${this.settingsService.configuration.url}${env.ImportDialogService.apiUrlImportFile}`;
        const formData = new FormData();

        formData.append('file', this.fileToUpload, this.fileToUpload.name);
        if (this.data.importConfiguration) {
            formData.append('configurationName', this.data.importConfiguration);
        }
        if (this.data.importApproval) {
            formData.append(
                'approvalRequired',
                this.data.importApproval.toString()
            );
        }
        if (this.data.additionalParams !== undefined) {
            formData.append('additionalParams', this.data.additionalParams);
        }

        this.http.postFormData(url, formData).subscribe(
            (response) => {
                this.snackBar.info('Plik został zaimportowany.');
                this.uploading = false;
                this.confirmFromDialog(response);
            },
            (err) => {
                this.uploading = false;
                this.snackBar.info(
                    err.error && err.error.Message ? err.error.Message : 'Problem podczas importu pliku. Sprawdź czy format pliku jest odpowiedni.'
                );
            },
        );
    }

    public fileChangeEvent(fileInput: any) {
        if (
            this.csv.isCSVFile(fileInput[0]) ||
            this.excel.isXlsxFile(fileInput[0])
        ) {
            if (
                this.data.importFileExt === 'csv' ||
                this.data.importFileExt === 'both' ||
                this.data.importFileExt === 'xlsx'
            ) {
                this.canImport = true;
                this.fileToUpload = fileInput[0];
                this.isFileValid = true;
                this.isHeadersValid = true;
                return;
            }
        }
        if (
            (fileInput[0].type === 'image/png' ||
            fileInput[0].type === 'image/jpg' ||
            fileInput[0].type === 'image/jpeg') &&
            this.data.importFileExt === 'img'
        ) {
            this.canImport = true;
            this.fileToUpload = fileInput[0];
            this.isFileValid = true;
            this.isHeadersValid = true;
            return;
        }
        this.snackBar.info('Plik posiada niewspierane rozszerzenie.');
        this.canImport = false;
    }
}
